import "./Table.scss";

import { Transition } from "@headlessui/react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { FCC } from "types";

import { Pagination } from "@/components/Interface/Pagination/Pagination";
import { FadeOverlayContainer } from "@/components/Layout/FadeOverlayContainer/FadeOverlayContainer";
import { LocalLoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";

interface Props {
  headline?: string;
  /** Omit to render no pagination — e.g. when the caller renders its own. */
  paginationPages?: number;
  noRows?: boolean;
  /** Defaults to `cockpit.deviceList.noSearchResults` when `noRows` is true. */
  noRowsMessage?: string;
  variant?: "plain" | "tile";
  hasPaddingBottom?: boolean;
  isLoading?: boolean;
}

export const Table: FCC<Props> = ({
  children,
  headline,
  paginationPages = 0,
  noRows = false,
  noRowsMessage,
  variant = "tile",
  hasPaddingBottom = true,
  isLoading = false,
}) => {
  const { t } = useTranslation("portal");

  return (
    <Transition
      data-testid="table-box"
      className={clsx("table-box", variant === "plain" && "table-box--plain")}
      appear={true}
      show={true}
      enter="transition-opacity ease-linear duration-150"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity ease-linear duration-300"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="table-shadow-box">
        {headline && (
          <div className="table-box-header">
            {/* Plain h2: index.scss styles it at 26px, and the Cockpit device
                list is the only caller. */}
            <h2>{headline}</h2>
          </div>
        )}
        {/* Does not accept a class name - since it gets modified programmatically */}
        <FadeOverlayContainer>
          <div className={clsx("relative", isLoading && "min-h-[200px]")}>
            <table className={clsx("table", isLoading && "opacity-50")}>
              {children}
            </table>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center pt-[64px]">
                <LocalLoadingSpinner />
              </div>
            )}
          </div>
        </FadeOverlayContainer>
        {noRows && (
          // Below tablet, the empty tbody leaves nothing to center the message
          // against, so mt-20 alone left it hugging the pagination row below
          // it instead of sitting centered in the empty area. flex/min-height
          // (same min-height as the isLoading state above) centers it there;
          // tablet and up keep the original fixed-margin layout unchanged.
          <div className="flex min-h-[200px] w-full items-center justify-center text-center text-base font-semibold text-foreground-primary tablet:mt-20 tablet:block tablet:min-h-0">
            {noRowsMessage ?? t("cockpit.deviceList.noSearchResults")}
          </div>
        )}
      </div>
      {paginationPages > 1 ? (
        <Pagination paginationPages={paginationPages} />
      ) : (
        hasPaddingBottom && <div className="pb-10" />
      )}
    </Transition>
  );
};
